<template>
  <BittsEmptyState
    v-if="showNoResults"
    :font-awesome-icon="['fak', 'search']"
    :font-awesome-icon-style="{ height: '26px', width: '26px' }"
    font-awesome-icon-class="team-table__empty-state-icon"
    title="No results"
    description="No users match your search"
    class="team-table__empty-state fade-in"
    data-testid="team-table-empty-state"
  />
  <BittsTable
    v-else
    :columns="columns"
    :enable-cell-text-selection="true"
    :row-selection="{
      mode: 'multiRow',
      enableClickSelection: false,
      checkboxes: false,
      headerCheckbox: false,
    }"
    :compress-columns="true"
    :passed-context="passedContext"
    :row-id="getRowId"
    selection-state="'all'"
    :rows="rows"
    :row-height="64"
    :pagination="true"
    :pagination-page-size="PAGE_SIZE"
    :hide-pagination="rows.length <= PAGE_SIZE"
    results-text="users"
    class="fade-in team-table"
  />
</template>

<script setup lang="ts">
import { BittsEmptyState, BittsTable } from '@crossbeam/bitts';

import { ColDef, GetRowIdParams, IRowNode } from '@ag-grid-community/core';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import TeamTableActionsCell from '@/components/team/TeamTableActionsCell.vue';
import TeamTableLoginMethodCell from '@/components/team/TeamTableLoginMethodCell.vue';
import TeamTableRoleCell from '@/components/team/TeamTableRoleCell.vue';
import TeamTableSeatCell from '@/components/team/TeamTableSeatCell.vue';
import TeamTableUserCell from '@/components/team/TeamTableUserCell.vue';

import useAuthorizationsWithRoles, {
  AuthOrInvite,
} from '@/composables/team/useAuthorizationsWithRoles';
import useAuth from '@/composables/useAuth';
import {
  LOGIN_METHOD_BY_PROVIDER,
  NO_ACCESS,
  sortRows,
} from '@/constants/team';
import { CORE, INTEGRATION, SALES } from '@/constants/team_v4';
import { useBillingStore, useRolesStore } from '@/stores';
import { shallowObjectSearch } from '@/utils';

const {
  search = '',
  seatTypes = [],
  userStatuses = [],
  fullAccessRoles = [],
  salesRoles = [],
  loginMethods = [],
  selectedIds = new Set(),
  isHeaderChecked = false,
  isHeaderIndeterminate = false,
} = defineProps<{
  search?: string;
  seatTypes?: string[];
  userStatuses?: string[];
  fullAccessRoles?: string[];
  salesRoles?: string[];
  loginMethods?: string[];
  selectedIds?: Set<number>;
  isHeaderChecked?: boolean;
  isHeaderIndeterminate?: boolean;
}>();

const emit = defineEmits<{
  pick: [id: number];
  pickAll: [checked: boolean];
}>();

const PAGE_SIZE = 50;

type Row = AuthOrInvite & { roleName?: string; email?: string };

function nameComparator(
  _a: string,
  _b: string,
  nodeA: IRowNode<Row>,
  nodeB: IRowNode<Row>,
) {
  const a = nodeA.data;
  const b = nodeB.data;
  const firstValue = a?.user ? a.user.first_name : a?.email;
  const secondValue = b?.user ? b.user.first_name : b?.email;
  if (firstValue === secondValue) return 0;
  return firstValue?.localeCompare(secondValue ?? '') ?? 0;
}

function seatComparator(_a: string, _b: string, nodeA: IRowNode<Row>) {
  return nodeA.data?.role_id ? 1 : -1;
}

function coreRoleComparator(
  _a: string,
  _b: string,
  nodeA: IRowNode<Row>,
  nodeB: IRowNode<Row>,
) {
  return sortRows(
    nodeA.data?.roleName?.toLowerCase(),
    nodeB.data?.roleName?.toLowerCase(),
  );
}

function salesRoleComparator(
  _a: string,
  _b: string,
  nodeA: IRowNode<Row>,
  nodeB: IRowNode<Row>,
) {
  return sortRows(
    nodeA.data?.sales_edge_role?.toLowerCase(),
    nodeB.data?.sales_edge_role?.toLowerCase(),
  );
}

function getRowId(params: GetRowIdParams<Row>) {
  const userId = params.data?.user?.id;
  if (userId) return `user-${userId}`;
  return `invite-${params.data?.id ?? params.data?.email}`;
}

const billingStore = useBillingStore();
const rolesStore = useRolesStore();
const { hasScope, userManagementEnabledForOrg } = useAuth();
const { isFreeTier } = storeToRefs(billingStore);

function getRowSeatType(row: Row) {
  const role = rolesStore.getRoleById(row.role_id || '');
  if (role?.seat_type === CORE) return CORE;
  if (role?.seat_type === INTEGRATION) return INTEGRATION;
  return SALES;
}

function getRowLoginMethod(row: Row) {
  return 'login_method' in row ? row.login_method : undefined;
}

function matchesFilters(row: Row) {
  if (seatTypes.length && !seatTypes.includes(getRowSeatType(row)))
    return false;
  if (
    userStatuses.length &&
    !userStatuses.includes(row.user ? 'active' : 'invited')
  )
    return false;
  if (
    (fullAccessRoles.length &&
      !(row.role_id && fullAccessRoles.includes(row.role_id))) ||
    (salesRoles.length &&
      !salesRoles.includes(row.sales_edge_role || NO_ACCESS))
  )
    return false;
  if (loginMethods.length) {
    const method = getRowLoginMethod(row);
    const canonicalMethod =
      method &&
      (LOGIN_METHOD_BY_PROVIDER[
        method as keyof typeof LOGIN_METHOD_BY_PROVIDER
      ] ??
        method);
    if (!(canonicalMethod && loginMethods.includes(canonicalMethod)))
      return false;
  }
  return true;
}

const canEditUsers = computed(
  () => hasScope('write:members') && userManagementEnabledForOrg.value,
);

const { authsWithRoles, invitesWithRoles } = useAuthorizationsWithRoles();

const searchedAuths = computed(() =>
  searchByFields(authsWithRoles.value, ['email', 'first_name', 'last_name']),
);
const searchedInvites = computed(() =>
  searchByFields(invitesWithRoles.value, ['email']),
);
const filteredAuths = computed(() =>
  searchedAuths.value.filter(matchesFilters),
);
const filteredInvites = computed(() =>
  searchedInvites.value.filter(matchesFilters),
);
const rows = computed(() => [...filteredAuths.value, ...filteredInvites.value]);

const hasActiveFilters = computed(
  () =>
    !!seatTypes.length ||
    !!userStatuses.length ||
    !!fullAccessRoles.length ||
    !!salesRoles.length ||
    !!loginMethods.length,
);
const showNoResults = computed(
  () => (search !== '' || hasActiveFilters.value) && rows.value.length === 0,
);

const selectableIds = computed(() =>
  filteredAuths.value
    .map((auth) => auth.user?.id)
    .filter((id): id is number => !!id),
);

const passedContext = {
  isUserSelected(userId: number | undefined) {
    if (!userId) return false;
    return selectedIds.has(userId);
  },
  handlePickUser(authOrInvite: AuthOrInvite) {
    const userId = authOrInvite.user?.id;
    if (!userId) return;
    emit('pick', userId);
  },
};

const columns = computed<ColDef[]>(() => {
  const base: ColDef[] = [
    {
      field: 'Name',
      cellRenderer: TeamTableUserCell,
      suppressMovable: true,
      autoHeight: true,
      sortable: true,
      sort: 'asc',
      headerComponentParams: {
        displayName: 'Name',
        showArrow: true,
        showCheckbox: canEditUsers.value,
        checkboxAriaLabel: 'Select all users',
        getChecked: () => isHeaderChecked,
        getIndeterminate: () => isHeaderIndeterminate,
        onChecked: (checked: boolean) => emit('pickAll', checked),
      },
      comparator: nameComparator,
      width: 300,
      suppressSizeToFit: true,
    },
    {
      field: 'Seat',
      cellRenderer: TeamTableSeatCell,
      suppressMovable: true,
      sortable: true,
      headerComponentParams: { displayName: 'Seat', showArrow: true },
      comparator: seatComparator,
      width: 160,
      suppressSizeToFit: true,
    },
    {
      field: 'Core Role',
      suppressMovable: true,
      headerComponentParams: {
        displayName: 'Full Access Role',
        showArrow: true,
      },
      cellRenderer: TeamTableRoleCell,
      sortable: true,
      comparator: coreRoleComparator,
    },
    {
      suppressMovable: true,
      field: 'Login Method',
      cellRenderer: TeamTableLoginMethodCell,
    },
    {
      suppressMovable: true,
      field: '',
      cellRenderer: TeamTableActionsCell,
    },
  ];

  if (!isFreeTier.value) {
    base.splice(3, 0, {
      field: 'Sales Role',
      suppressMovable: true,
      headerComponentParams: {
        displayName: 'Sales Role',
        icon: ['fak', 'sales'],
        showArrow: true,
      },
      cellRenderer: TeamTableRoleCell,
      sortable: true,
      comparator: salesRoleComparator,
    });
  }
  return base;
});

function searchByFields(invitesOrAuths: AuthOrInvite[], fields: string[]) {
  return search === ''
    ? invitesOrAuths
    : invitesOrAuths.filter((inviteOrAuth) =>
        shallowObjectSearch(inviteOrAuth.user ?? inviteOrAuth, fields, search),
      );
}

defineExpose({ searchedAuths, selectableIds });
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";
.team-table {
  .ag-cell {
    padding: 0;
  }

  .bulk-actions,
  .interactivity {
    @apply flex justify-between items-end gap-8 ml-8;
  }
}

.team-table__empty-state {
  @apply py-40;
}
</style>

<style lang="pcss">
@reference "../../css/theme.css";
.team-table__empty-state-icon {
  @apply text-neutral-text-weak p-20 rounded-[36px];
  background: radial-gradient(
      136.96% 69.92% at 50.09% -12.42%,
      theme(colors.neutral.accent / 0.2) 0%,
      theme(colors.neutral.accent / 0) 100%
    ),
    linear-gradient(0deg, theme(colors.white), theme(colors.white));
}

.team-table-disabled-tooltip {
  .ant-tooltip-content {
    @apply max-w-[200px];
  }
}

.team-table {
  .ag-header-cell .svg-inline--fa.fa-sales {
    @apply text-success-accent pr-2;
  }

  .ag-row .ag-cell:last-of-type {
    @apply justify-end;
  }
}
</style>
